import { useCallback, useRef } from "react";
import { pricingUtils } from "@flights/web-api-utils-universal";
import useTrackerContext from "../useTrackerContext.js";
import useGlobalContext from "../useGlobalContext.js";
import useRouteName from "../useRouteName.js";
const ACTION_NAME = "flights__price_rendered";
const ACTION_VERSION = "5.1.0";
const buildEventSignature = (uiComponent, productId, ancillaries, totalPrice) => {
    const ancillariesSignature = ancillaries
        .map(({ id, quantity }) => `${id}_${quantity}`)
        .sort()
        .join("-");
    return [
        uiComponent,
        productId,
        ancillariesSignature,
        totalPrice.currencyCode,
        totalPrice.units,
        totalPrice.nanos
    ].join("-");
};
export const usePriceConsistencyMonitoringEvents = () => {
    const { trackExternal } = useTrackerContext();
    const routName = useRouteName();
    const { requestId } = useGlobalContext();
    const lastTrackedRef = useRef("");
    return useCallback((uiComponent, productId, productPrices, totalPrice, pricePerAdult, ancillaries, isPaymentPriceChange) => {
        const eventSignature = buildEventSignature(uiComponent, productId, ancillaries, totalPrice);
        if (lastTrackedRef.current === eventSignature) {
            return;
        }
        lastTrackedRef.current = eventSignature;
        const trackingData = { requestId };
        const content = getContent(isPaymentPriceChange ? "checkout-payment-price-change" : routName, trackingData, uiComponent, productId, productPrices, totalPrice, pricePerAdult, ancillaries);
        trackExternal({
            type: "c360",
            args: [
                {
                    action_name: ACTION_NAME,
                    action_version: ACTION_VERSION,
                    content
                }
            ]
        });
    }, [requestId, routName, trackExternal]);
};
const getContent = (routeName, trackingData, uiComponent, productId, productPrices, totalPrice, pricePerAdult, ancillaries) => {
    const items = productPrices.map(({ productPrice, productType }) => ({
        item_price: {
            currency_code: productPrice.currencyCode,
            price: String(pricingUtils.priceToValue(productPrice))
        },
        item_type: productType
    }));
    const total = {
        currency_code: totalPrice.currencyCode,
        price: String(pricingUtils.priceToValue(totalPrice))
    };
    const perAdultPrice = pricePerAdult
        ? {
            currency_code: pricePerAdult.currencyCode,
            price: String(pricingUtils.priceToValue(pricePerAdult))
        }
        : undefined;
    const attachedAncillaries = ancillaries
        .sort((a, b) => a.id.localeCompare(b.id))
        .map(({ id, quantity }) => ({
        ancillary_name: id,
        ancillary_quantity: quantity
    }));
    return {
        funnel_page: routeName,
        product_id: productId,
        price_breakdown_items: items,
        request_id: trackingData.requestId,
        total_price: total,
        per_adult_price: perAdultPrice,
        attached_ancillaries: attachedAncillaries,
        ui_component: uiComponent
    };
};
export default usePriceConsistencyMonitoringEvents;
